<template>
  <div class="fcm-push-test">
    <b-card>
      <b-card-title>FCM Push Notification Test</b-card-title>
      <b-card-sub-title class="mb-1">
        Dev harness — registers your device token and sends test pushes via
        <code>POST /api/notify/push</code> (no server keys in the browser).
      </b-card-sub-title>

      <b-alert v-if="!isDevBuild" variant="warning" show class="mt-1">
        This page is only available in development builds.
      </b-alert>

      <template v-else>
        <!-- Status panel -->
        <b-card bg-variant="light" class="mt-1 mb-2">
          <h5 class="mb-75">
            Current session
          </h5>
          <b-row>
            <b-col md="6">
              <dl class="mb-0 fcm-status-dl">
                <dt>User</dt>
                <dd>{{ currentUserLabel }}</dd>
                <dt>Notification permission</dt>
                <dd>
                  <b-badge :variant="permissionVariant">{{ permissionStatus }}</b-badge>
                </dd>
                <dt>Local FCM token</dt>
                <dd class="text-break small">
                  {{ localTokenPreview }}
                </dd>
              </dl>
            </b-col>
            <b-col md="6">
              <dl class="mb-0 fcm-status-dl">
                <dt>Server tokens (GET /user/token/:id)</dt>
                <dd>
                  <span v-if="serverTokensLoading">Loading…</span>
                  <span v-else-if="!serverTokenRows.length" class="text-muted">None registered</span>
                  <ul v-else class="pl-1 mb-0 small">
                    <li v-for="(row, idx) in serverTokenRows" :key="idx">
                      {{ truncateToken(row.fcm_token || row.token) }}
                    </li>
                  </ul>
                </dd>
                <dt>Last registration</dt>
                <dd>{{ lastRegisterResult }}</dd>
              </dl>
            </b-col>
          </b-row>
          <div class="d-flex flex-wrap mt-1">
            <b-button
              variant="primary"
              class="mr-1 mb-50"
              :disabled="busy"
              @click="registerToken"
            >
              Register my token
            </b-button>
            <b-button
              variant="outline-primary"
              class="mr-1 mb-50"
              :disabled="busy"
              @click="refreshServerTokens"
            >
              Refresh server tokens
            </b-button>
            <b-button
              variant="outline-secondary"
              class="mb-50"
              :disabled="busy"
              @click="refreshPermission"
            >
              Refresh status
            </b-button>
          </div>
        </b-card>

        <!-- Push form -->
        <b-form @submit.prevent="sendToTarget">
          <b-row>
            <b-col md="6">
              <b-form-group label="Title" label-for="fcm-title">
                <b-form-input
                  id="fcm-title"
                  v-model="form.title"
                  required
                  placeholder="Test notification title"
                />
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group label="Body" label-for="fcm-body">
                <b-form-input
                  id="fcm-body"
                  v-model="form.body"
                  placeholder="Optional body text"
                />
              </b-form-group>
            </b-col>
            <b-col md="4">
              <b-form-group label="Target user ID" label-for="fcm-user-id">
                <b-form-input
                  id="fcm-user-id"
                  v-model="form.userId"
                  type="number"
                  placeholder="users.id"
                />
              </b-form-group>
            </b-col>
            <b-col md="4">
              <b-form-group label="Target emp ID (optional)" label-for="fcm-emp-id">
                <b-form-input
                  id="fcm-emp-id"
                  v-model="form.empId"
                  placeholder="emp_id fallback"
                />
              </b-form-group>
            </b-col>
            <b-col md="4">
              <b-form-group label="Module" label-for="fcm-module">
                <b-form-select
                  id="fcm-module"
                  v-model="form.module"
                  :options="moduleOptions"
                />
              </b-form-group>
            </b-col>
            <b-col md="4">
              <b-form-group label="Type" label-for="fcm-type">
                <b-form-input
                  id="fcm-type"
                  v-model="form.type"
                  placeholder="e.g. task_ping"
                />
              </b-form-group>
            </b-col>
            <b-col md="4">
              <b-form-group label="Task ID (optional)" label-for="fcm-task-id">
                <b-form-input
                  id="fcm-task-id"
                  v-model="form.taskId"
                  placeholder="For pm_tasks deep links"
                />
              </b-form-group>
            </b-col>
            <b-col md="4">
              <b-form-group label="Link override (optional)" label-for="fcm-link">
                <b-form-input
                  id="fcm-link"
                  v-model="form.link"
                  placeholder="Full URL; auto-built if empty"
                />
              </b-form-group>
            </b-col>
          </b-row>

          <div class="d-flex flex-wrap">
            <b-button
              type="button"
              variant="success"
              class="mr-1 mb-50"
              :disabled="busy"
              @click="sendToMe"
            >
              Send test push to me
            </b-button>
            <b-button
              type="submit"
              variant="warning"
              class="mr-1 mb-50"
              :disabled="busy || !hasTarget"
            >
              Send test push to target
            </b-button>
          </div>
        </b-form>

        <!-- Last result -->
        <b-card v-if="lastSendResult" bg-variant="light" class="mt-2">
          <h5 class="mb-50">
            Last send result
          </h5>
          <pre class="mb-0 small fcm-result-pre">{{ lastSendResult }}</pre>
        </b-card>

        <b-alert variant="info" show class="mt-2 mb-0">
          <strong>Console helpers:</strong> in dev, <code>window.__fcmTest</code> exposes
          <code>register()</code>, <code>sendToMe()</code>, <code>sendToUser(userId, opts)</code>.
          CLI: <code>node scripts/test-fcm-push.js --help</code>
        </b-alert>
      </template>
    </b-card>
  </div>
</template>

<script>
import {
  BAlert,
  BBadge,
  BButton,
  BCard,
  BCardSubTitle,
  BCardTitle,
  BCol,
  BForm,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BRow,
} from 'bootstrap-vue'
import UserService from '@/services/user-service'
import {
  ensureFcmReady,
  registerFcmTokenForCurrentUser,
  sendPushToUserId,
  sendPushToEmpId,
  buildTasksDeepLink,
} from '@/utils/fcm-push'

const MODULE_OPTIONS = [
  { value: 'pm_tasks', text: 'pm_tasks (Tasks)' },
  { value: 'announcements', text: 'announcements' },
  { value: 'jobs', text: 'jobs' },
  { value: 'candidates', text: 'candidates' },
  { value: 'exotel', text: 'exotel (calls)' },
  { value: 'general', text: 'general' },
]

export default {
  name: 'FcmPushTest',
  components: {
    BAlert,
    BBadge,
    BButton,
    BCard,
    BCardSubTitle,
    BCardTitle,
    BCol,
    BForm,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BRow,
  },
  data() {
    return {
      isDevBuild: process.env.NODE_ENV !== 'production',
      busy: false,
      permissionStatus: 'unknown',
      localToken: '',
      serverTokenRows: [],
      serverTokensLoading: false,
      lastRegisterResult: '—',
      lastSendResult: '',
      form: {
        title: 'FCM test push',
        body: 'Hello from the dev FCM harness',
        userId: '',
        empId: '',
        module: 'pm_tasks',
        type: 'task_ping',
        taskId: '',
        link: '',
      },
      moduleOptions: MODULE_OPTIONS,
    }
  },
  computed: {
    currentUser() {
      try {
        return JSON.parse(localStorage.getItem('userData') || 'null')
      } catch (e) {
        return null
      }
    },
    currentUserLabel() {
      const u = this.currentUser
      if (!u) return 'Not logged in'
      const name = [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email || 'User'
      return `${name} (id: ${u.id}, emp: ${u.emp_id ?? u.empId ?? '—'})`
    },
    permissionVariant() {
      if (this.permissionStatus === 'granted') return 'success'
      if (this.permissionStatus === 'denied') return 'danger'
      return 'secondary'
    },
    localTokenPreview() {
      if (!this.localToken) return '—'
      return this.truncateToken(this.localToken)
    },
    hasTarget() {
      return !!(this.form.userId || this.form.empId)
    },
  },
  mounted() {
    if (!this.isDevBuild) return
    ensureFcmReady(this)
    this.refreshPermission()
    this.prefillTargetUser()
    this.refreshServerTokens()
    this.exposeConsoleHelpers()
  },
  beforeDestroy() {
    if (typeof window !== 'undefined' && window.__fcmTest) {
      delete window.__fcmTest
    }
  },
  methods: {
    prefillTargetUser() {
      const u = this.currentUser
      if (u && u.id != null && !this.form.userId) {
        this.form.userId = String(u.id)
      }
    },
    truncateToken(token) {
      if (!token) return '—'
      const s = String(token)
      if (s.length <= 24) return s
      return `${s.slice(0, 12)}…${s.slice(-8)}`
    },
    refreshPermission() {
      if (typeof Notification === 'undefined') {
        this.permissionStatus = 'unsupported'
        return
      }
      this.permissionStatus = Notification.permission
    },
    async registerToken() {
      this.busy = true
      this.lastRegisterResult = 'Registering…'
      try {
        ensureFcmReady(this)
        const res = await registerFcmTokenForCurrentUser()
        if (res.token) this.localToken = res.token
        this.lastRegisterResult = JSON.stringify(res, null, 2)
        this.refreshPermission()
        await this.refreshServerTokens()
        if (res.ok) {
          this.$toast.success('FCM token registered')
        } else {
          const hint = res.remediation && res.remediation.length
            ? ` — see console for Firebase Console steps`
            : ''
          this.$toast.warning(`Registration: ${res.reason || 'failed'}${hint}`)
        }
      } catch (err) {
        this.lastRegisterResult = String(err && err.message ? err.message : err)
        this.$toast.error('Registration failed')
      } finally {
        this.busy = false
      }
    },
    async refreshServerTokens() {
      const u = this.currentUser
      if (!u || u.id == null) return
      this.serverTokensLoading = true
      try {
        const res = await UserService.getToken(u.id)
        this.serverTokenRows = this.extractTokenRows(res)
      } catch (err) {
        this.serverTokenRows = []
        console.warn('[fcm-test] getToken failed:', err)
      } finally {
        this.serverTokensLoading = false
      }
    },
    extractTokenRows(res) {
      if (!res) return []
      if (Array.isArray(res)) return res
      if (Array.isArray(res.data)) return res.data
      if (res.status === 'OK' && Array.isArray(res.data)) return res.data
      const row = res.data && typeof res.data === 'object' ? res.data : res
      if (row && (row.fcm_token || row.token)) return [row]
      return []
    },
    buildPayloadOptions() {
      const data = {
        module: this.form.module || 'general',
        type: this.form.type || 'test',
      }
      if (this.form.taskId) data.taskId = String(this.form.taskId)
      const link = this.form.link
        || (this.form.module === 'pm_tasks' && this.form.taskId
          ? buildTasksDeepLink(this.form.taskId)
          : undefined)
      return {
        title: this.form.title,
        body: this.form.body,
        link,
        data,
      }
    },
    recordSendResult(label, result) {
      this.lastSendResult = `${label}\n${JSON.stringify(result, null, 2)}`
    },
    async sendToMe() {
      const u = this.currentUser
      if (!u || u.id == null) {
        this.$toast.error('Log in first')
        return
      }
      this.busy = true
      try {
        const opts = this.buildPayloadOptions()
        const result = await sendPushToUserId(u.id, opts)
        this.recordSendResult(`sendToMe (userId=${u.id})`, result)
        if (result.sent) {
          this.$toast.success(`Push sent (${result.sent})`)
        } else {
          this.$toast.warning(`Push skipped: ${result.reason || 'unknown'}`)
        }
      } finally {
        this.busy = false
      }
    },
    async sendToTarget() {
      if (!this.hasTarget) {
        this.$toast.warning('Enter a target user ID or emp ID')
        return
      }
      this.busy = true
      try {
        const opts = this.buildPayloadOptions()
        let result
        if (this.form.userId) {
          result = await sendPushToUserId(this.form.userId, opts)
          this.recordSendResult(`sendToUserId(${this.form.userId})`, result)
        } else {
          result = await sendPushToEmpId(this.form.empId, opts)
          this.recordSendResult(`sendToEmpId(${this.form.empId})`, result)
        }
        if (result.sent) {
          this.$toast.success(`Push sent (${result.sent})`)
        } else {
          this.$toast.warning(`Push skipped: ${result.reason || 'unknown'}`)
        }
      } finally {
        this.busy = false
      }
    },
    exposeConsoleHelpers() {
      if (typeof window === 'undefined') return
      const vm = this
      window.__fcmTest = {
        register: () => vm.registerToken(),
        sendToMe: () => vm.sendToMe(),
        sendToUser: (userId, opts = {}) => {
          vm.form.userId = String(userId)
          if (opts.title) vm.form.title = opts.title
          if (opts.body) vm.form.body = opts.body
          if (opts.module) vm.form.module = opts.module
          if (opts.type) vm.form.type = opts.type
          if (opts.taskId) vm.form.taskId = String(opts.taskId)
          if (opts.link) vm.form.link = opts.link
          return vm.sendToTarget()
        },
        getStatus: () => ({
          permission: vm.permissionStatus,
          localToken: vm.localToken,
          serverTokens: vm.serverTokenRows,
          lastRegister: vm.lastRegisterResult,
          lastSend: vm.lastSendResult,
        }),
      }
    },
  },
}
</script>

<style scoped>
.fcm-status-dl dt {
  font-size: 0.75rem;
  text-transform: uppercase;
  color: #6e6b7b;
  margin-bottom: 0.15rem;
}
.fcm-status-dl dd {
  margin-bottom: 0.65rem;
}
.fcm-result-pre {
  max-height: 240px;
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-word;
}
</style>
